"use client";

import { motion } from "framer-motion";
import { useState } from "react";
import { useLeadModalStore } from "@/stores/useLeadModalStore";
import { UserPlusIcon } from "./icons/UserPlusIcon";
import { UserStarIcon } from "./icons/UserStarIcon";
import { CopyIcon } from "./icons/CopyIcon";
import { TrendingUpDownIcon } from "./icons/TrendingUpDownIcon";
import { ArrowRight } from "lucide-react";
import { useT } from "@/hooks/useT";

const STEP_KEYS = [
  { number: "01", titleKey: "how.step1.title", descKey: "how.step1.desc", iconKey: "userplus" },
  { number: "02", titleKey: "how.step2.title", descKey: "how.step2.desc", iconKey: "userstar" },
  { number: "03", titleKey: "how.step3.title", descKey: "how.step3.desc", iconKey: "copy" },
  { number: "04", titleKey: "how.step4.title", descKey: "how.step4.desc", iconKey: "trending" },
] as const;

function StepIcon({ iconKey, isHovered }: { iconKey: string; isHovered: boolean }) {
  const iconProps = { size: 32, className: "text-gray-900 dark:text-white", isHovered };
  switch (iconKey) {
    case "userplus":
      return <UserPlusIcon {...iconProps} />;
    case "userstar":
      return <UserStarIcon {...iconProps} />;
    case "copy":
      return <CopyIcon {...iconProps} />;
    case "trending":
      return <TrendingUpDownIcon {...iconProps} />;
    default:
      return null;
  }
}

export default function HowItWorksSection() {
  const t = useT();
  const { open } = useLeadModalStore();
  const [hoveredStep, setHoveredStep] = useState<string | null>(null);
  const steps = STEP_KEYS.map(s => ({ ...s, title: t(s.titleKey), description: t(s.descKey) }));

  return (
    <section className="relative py-10 sm:py-16 lg:py-24 bg-white dark:bg-[#0f0f12] overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 sm:mb-16"
        >
          <p className="text-sm font-monument font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
            {t("how.eyebrow")}
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-monument font-bold text-gray-900 dark:text-white mb-4">
            {t("how.headline.before")}{" "}
            <span className="text-accent-primary">
              {t("how.headline.accent")}
            </span>
          </h2>
          <p className="text-lg text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
            {t("how.subtitle")}
          </p>
        </motion.div>

        {/* Steps */}
        <div className="relative">
          {/* Connection line - desktop only */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-px -translate-y-1/2">
            <div className="h-full bg-gray-200 dark:bg-[#1A1A1A]" />
          </div>

          {/* Steps grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8 relative">
            {steps.map((step, index) => {
              const isHovered = hoveredStep === step.number;
              return (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className="relative group"
                  onMouseEnter={() => setHoveredStep(step.number)}
                  onMouseLeave={() => setHoveredStep(null)}
                >
                  <div className="relative h-full bg-white dark:bg-[#18181D] rounded-[5px] p-4 sm:p-6 border border-gray-200 dark:border-[#1A1A1A] hover:bg-gray-50 dark:hover:bg-[#1e1e24] transition-colors">
                    {/* Number badge */}
                    <div className="absolute -top-4 -right-4 w-12 h-12 bg-gray-900 dark:bg-white rounded-[5px] flex items-center justify-center">
                      <span className="text-white dark:text-gray-900 font-inter-tight font-black text-sm">{step.number}</span>
                    </div>

                    {/* Icon */}
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] mb-6">
                      <StepIcon iconKey={step.iconKey} isHovered={isHovered} />
                    </div>

                    {/* Content */}
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                      {step.title}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-8 sm:mt-16"
        >
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            {t("how.bottomNote")}
          </p>
          <button
            onClick={open}
            aria-label="Start Polymarket copy trading now"
            className="inline-flex items-center gap-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-8 py-4 rounded-[5px] text-base font-inter-tight font-bold hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
          >
            {t("copytrading.hero.ctaPrimary")}
            <ArrowRight className="w-5 h-5" />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
